import React, { useState, useEffect } from 'react';
import { MessageSquare, X, Send } from 'lucide-react';
import Button from '../../../../components/common/Button/Button';
import quotationService from '../../../../api/crm/quotations/quotationService';
import { handleTextInputChange, getCharacterCount, FIELD_LIMITS } from '../../../../utils/inputValidation';

const WhatsAppModal = ({
    isOpen,
    onClose,
    onSend,
    quoteId,
    isSending = false,
    hideTypeSelection = false
}) => {
    const [formData, setFormData] = useState({
        type: hideTypeSelection ? 'custom' : '', // 'invoice' or 'quotation' or 'custom'
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const MIN_MESSAGE_LENGTH = 10;
    const MAX_MESSAGE_LENGTH = FIELD_LIMITS.message || 1000;

    useEffect(() => {
        if (isOpen) {
            // Reset form on open
            setFormData({
                type: hideTypeSelection ? 'custom' : '',
                message: ''
            });
            setErrors({});
            setTouched({});
        }
    }, [isOpen, hideTypeSelection]);

    if (!isOpen) return null;

    const validate = (data) => {
        const newErrors = {};

        // Radio button validation
        if (!hideTypeSelection && !data.type) {
            newErrors.type = 'Please select either Invoice or Quotation';
        }

        if (hideTypeSelection) {
            if (!data.message || data.message.trim() === '') {
                newErrors.message = 'Message is required';
            } else if (data.message.trim().length < MIN_MESSAGE_LENGTH) {
                newErrors.message = `Message must be at least ${MIN_MESSAGE_LENGTH} characters`;
            } else if (data.message.length > MAX_MESSAGE_LENGTH) {
                newErrors.message = `Message must be ${MAX_MESSAGE_LENGTH} characters or less`;
            }
        }

        return newErrors;
    };

    const handleChange = (field, value) => {
        const newData = { ...formData, [field]: value };
        setFormData(newData);

        if (touched[field]) {
            const validationErrors = validate(newData);
            setErrors(validationErrors);
        }
    };

    const handleBlur = (field) => {
        setTouched({ ...touched, [field]: true });
        const validationErrors = validate(formData);
        setErrors(validationErrors);
    };

    const handleSendClick = () => {
        setTouched({ type: true, message: true });
        const validationErrors = validate(formData);

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        onSend(formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 px-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-600 to-green-500 px-6 py-4 flex items-center justify-between text-white">
                    <div className="flex items-center space-x-2">
                        <MessageSquare className="h-6 w-6" />
                        <h3 className="text-xl font-bold tracking-tight">WhatsApp Message</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-white/20 rounded-full transition-colors"
                        aria-label="Close"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Radio Buttons */}
                    {!hideTypeSelection && (
                        <div className="space-y-4">
                            <label className="block text-sm font-semibold text-gray-700">
                                Select Document Type <span className="text-red-500">*</span>
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                                <label
                                    className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${formData.type === 'invoice'
                                        ? 'border-green-500 bg-green-50 shadow-sm'
                                        : 'border-gray-200 hover:border-green-200'
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        name="type"
                                        value="invoice"
                                        checked={formData.type === 'invoice'}
                                        onChange={(e) => handleChange('type', e.target.value)}
                                        onBlur={() => handleBlur('type')}
                                        className="h-4 w-4 text-green-600 border-gray-300 focus:ring-green-500 cursor-pointer"
                                    />
                                    <span className={`ml-3 font-bold ${formData.type === 'invoice' ? 'text-green-700' : 'text-gray-600'}`}>
                                        Invoice
                                    </span>
                                </label>
                                <label
                                    className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${formData.type === 'quotation'
                                        ? 'border-green-500 bg-green-50 shadow-sm'
                                        : 'border-gray-200 hover:border-green-200'
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        name="type"
                                        value="quotation"
                                        checked={formData.type === 'quotation'}
                                        onChange={(e) => handleChange('type', e.target.value)}
                                        onBlur={() => handleBlur('type')}
                                        className="h-4 w-4 text-green-600 border-gray-300 focus:ring-green-500 cursor-pointer"
                                    />
                                    <span className={`ml-3 font-bold ${formData.type === 'quotation' ? 'text-green-700' : 'text-gray-600'}`}>
                                        Quotation
                                    </span>
                                </label>
                            </div>
                            {touched.type && errors.type && (
                                <p className="text-xs text-red-500 mt-1 flex items-center gap-1 font-medium">
                                    <span className="w-1 h-1 bg-red-500 rounded-full" /> {errors.type}
                                </p>
                            )}
                        </div>
                    )}

                    {hideTypeSelection && (
                        <div className="space-y-3">
                            <label className="block text-sm font-semibold text-gray-700">
                                Message <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <textarea
                                    rows={4}
                                    value={formData.message}
                                    onChange={(e) => {
                                        const value = handleTextInputChange(e, null, MAX_MESSAGE_LENGTH);
                                        handleChange('message', value);
                                    }}
                                    onBlur={() => handleBlur('message')}
                                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all resize-none ${touched.message && errors.message
                                        ? 'border-red-300 focus:ring-2 focus:ring-red-100'
                                        : 'border-gray-200 focus:ring-2 focus:ring-green-100 focus:border-green-500'
                                        }`}
                                    placeholder="Enter your WhatsApp message..."
                                />
                                <div className={`absolute bottom-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded ${formData.message.length > MAX_MESSAGE_LENGTH * 0.9
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-gray-100 text-gray-500'
                                    }`}>
                                    {getCharacterCount(formData.message, MAX_MESSAGE_LENGTH)}
                                </div>
                            </div>
                            {touched.message && errors.message ? (
                                <p className="text-xs text-red-500 mt-1 flex items-center gap-1 font-medium">
                                    <span className="w-1 h-1 bg-red-500 rounded-full" /> {errors.message}
                                </p>
                            ) : (
                                <p className="text-xs text-gray-400">
                                    Min: {MIN_MESSAGE_LENGTH} characters. Max: {MAX_MESSAGE_LENGTH} characters.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="bg-gray-50 px-6 py-4 flex items-center justify-end border-t border-gray-100">
                    <div className="flex items-center space-x-3">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            className="px-6 border-2 border-gray-200 font-semibold text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-all active:scale-95"
                            disabled={isSending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSendClick}
                            className="px-8 bg-green-600 hover:bg-green-700 text-white font-bold shadow-lg shadow-green-200 transition-all hover:translate-y-[-1px] active:scale-95 disabled:translate-y-0 disabled:shadow-none"
                            icon={<Send className="h-4 w-4" />}
                            disabled={isSending}
                        >
                            {isSending ? 'Sending...' : 'Send'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WhatsAppModal;
